// ============================================================================
//   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
// ----------------------------------------------------------------------------
//   costNotes (Spec 256 — Backup Cost Governance)
//   Directional cost notes for the Cost & Controls tab. FR-008/FR-022: state
//   that an option affects cost and WHERE, across all backup cost dimensions —
//   NEVER an absolute currency amount. Plain language, for operators.
// ============================================================================

/** Cost dimensions a backup feature/route can drive (no currency implied). */
export type CostDimension = 'egress' | 'transfer' | 'storage' | 'compute' | 'transactions';

export interface CostNote {
  /** Plain-language, directional. No numbers, no currency. */
  text: string;
  /** Which cost dimension(s) this drives. */
  dimensions: CostDimension[];
}

/** Notes for the three control switches. */
export const CONTROL_COST_NOTES: Record<'worm' | 'verification' | 'secondaryCopy', CostNote> = {
  worm: {
    text: 'Keeps every backup version immutable for the lock window — locked data can’t be reclaimed until the lock expires, so stored data (and storage cost) grows.',
    dimensions: ['storage'],
  },
  verification: {
    text: 'Periodically restores backups to prove they recover — each run adds compute and reads/transfers the data.',
    dimensions: ['compute', 'transfer'],
  },
  secondaryCopy: {
    text: 'Duplicates each backup to a second location (including the isolated vault) — adds duplicate storage, and outbound egress when the copy crosses regions or the network.',
    dimensions: ['storage', 'egress'],
  },
};

/** Notes for the network-route classification. */
export const ROUTE_COST_NOTES: Record<string, CostNote> = {
  'cross-region': {
    text: 'Backup data crosses Azure regions — this incurs cross-region data-transfer cost.',
    dimensions: ['transfer'],
  },
  'nat-internet': {
    text: 'Traffic egresses through the NAT gateway (streaming mover) — the main driver of the egress bill.',
    dimensions: ['egress'],
  },
  'private-endpoint': {
    text: 'The write leg uses a private endpoint — cheaper than public egress, but the OneLake read leg still egresses when streaming.',
    dimensions: ['egress'],
  },
  'server-side': {
    text: 'The destination pulls data directly (server-side) — avoids routing bytes through the app/NAT gateway.',
    dimensions: [],
  },
  unknown: {
    text: 'The route could not be classified from collected data — treat cost as unknown, not cheap.',
    dimensions: [],
  },
};

/** Generic dimension labels for the offenders / advisor legends (still non-currency). */
export const DIMENSION_LABEL: Record<CostDimension, string> = {
  egress: 'Outbound egress',
  transfer: 'Cross-region transfer',
  storage: 'Stored data',
  compute: 'Backup / restore compute',
  transactions: 'Storage operations',
};

/** Resolve the note for a control; returns undefined for an unknown key. */
export function controlNote(key: keyof typeof CONTROL_COST_NOTES): CostNote {
  return CONTROL_COST_NOTES[key];
}

/** Resolve the note for a route path-axis (falls back to 'unknown'). */
export function routeNote(pathOrRegionKey: string): CostNote {
  return ROUTE_COST_NOTES[pathOrRegionKey] ?? ROUTE_COST_NOTES.unknown;
}
